/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IDropdownOption } from '@fluentui/react/lib/components/Dropdown';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash/orderBy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { ResourceAssignmentForRegionSwagger } from 'src/core/services/resourceAssignmentSwaggers';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { getResourceInfo } from 'src/core/utils/resourceAssignmentUtils';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { DeployedModelsTable } from 'src/modules/modelDeploy/DeployedModelsTable';
import { DeployedModelsTableCommandBar } from 'src/modules/modelDeploy/DeployedModelsTableCommandBar';
import { DeployedModelUrlModal } from 'src/modules/modelDeploy/DeployedModelUrlModal';
import { DeployModelModal } from 'src/modules/modelDeploy/DeployModelModal';
import { DeploymentStore, ModelDeployLayoutProps } from 'src/modules/modelDeploy/ModelDeployLayout';
import { RuntimeCardsSection } from 'src/modules/modelDeploy/RuntimeCardsSection';
import { SwapDeploymentsModal } from 'src/modules/modelDeploy/SwapDeploymentsModal';
import { getGithubSamples, getSdkLink } from 'src/modules/modelDeploy/utils/runtimeCardsUtils';

type DeploymentTabProps = ModelDeployLayoutProps;

const messages = defineMessages({
    emptyMsg: {
        id: 'modelDeployLayout.deploymentTab.emptyMsg',
        defaultMessage: "You don't have any deployed models. Submit a new deployment by clicking on"
    },
    startDeployment: { id: 'modelDeployLayout.deploymentTab.startDeployment', defaultMessage: 'Add deployment' },
    runtimeGithubCardLinkText: { id: 'modelDeployLayout.deploymentTab.runtimeGithubCardLinkText', defaultMessage: 'Azure SDK sample' },
    deletingDeploymentTitle: {
        id: 'modelDeployLayout.deploymentTab.deletingDeploymentTitle',
        defaultMessage: 'Delete selected deployment(s)'
    },
    deletingDeploymentBody: {
        id: 'modelDeployLayout.deploymentTab.deletingDeploymentBody',
        defaultMessage: 'Are you sure you want to delete the selected deployment(s)?'
    }
});

const emptyStateTheme = getScopedTheme('EmptyState');
const { styleComponent } = getStylist('ModelDeployLayout');
const TableContainer = styleComponent<IStackProps>(Stack)('TableContainer', { overflow: 'hidden' });

const runtimeCardsSectionStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { width: '100%', bottom: '10px', marginTop: '10px', paddingTop: '20px' }
});

export const DeploymentTab = observer((props: DeploymentTabProps) => {
    const {
        predictUrl,
        submitCode,
        projectType,
        retrieveCode,
        onDeploySubmit,
        onSwapDeployments,
        isLoadingResources,
        onDeleteDeployment,
        onRenderDeployModal,
        isServiceKeyTypeValid,
        deploymentsStoreHouse,
        trainedModelStoreHouse,
        onChangeSelectedResource,
        deletedDeploymentsIds = [],
        regionDeploymentStoreHouse,
        isMultiRegionDeploymentEnabled,
        isOrchestrationProjectConnectedToLUISOrQna
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();
    const { selection, selectedItems } = useSelection<DeploymentStore>();
    const deploymentTracker = useProgressTracker({
        onComplete: () => updateModalStates({ isDeployModalHidden: true })
    });
    const swapTracker = useProgressTracker({
        onComplete: () => updateModalStates({ isSwapDeploymentsModalHidden: true })
    });

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canDeployProject } = mapUserRoleToUserActions(userRole);

    const [query, setQuery] = React.useState('');
    const [isDeleteModalHidden, setIsDeleteModalHidden] = React.useState(true);
    const [sortKey, setSortKey] = React.useState<SortKeyType<DeploymentStore>>({
        key: 'lastDeployedDateTime',
        order: 'desc'
    });
    const [modalStates, updateModalStates] = useUpdateState({
        isDeployModalHidden: true,
        isEndpointURLModalHidden: true,
        isSwapDeploymentsModalHidden: true
    });

    const [selectedResource, setSelectedResource] = React.useState<string>(null);
    const [resourceOptions, setResourceOptions] = React.useState<IDropdownOption[]>([]);
    const isInEmptyState = deploymentsStoreHouse.tracker.isCompleted && !deploymentsStoreHouse.items.length;
    const noTrainedModelsFound = trainedModelStoreHouse.items.length === 0;
    const deployments = deploymentsStoreHouse.items;

    const projectIdForTelemetry = rootStore.currentCluProjectStore?.id ?? rootStore.currentCustomTextProjectStore?.id;

    const filteredModels = orderBy(
        substringSearchStrategy(deployments, query, ['deploymentName', 'modelName']),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    const handleResourceChange = (resourceId: string) => {
        setSelectedResource(resourceId);
        onChangeSelectedResource(resourceId);
    };

    const handleDeploy = (deploymentInfo: {
        deploymentName: string;
        modelName: string;
        resources: ResourceAssignmentForRegionSwagger[];
    }) => {
        logButtonClick(serviceName, 'DeployModel', 'SubmitDeployment', null, projectIdForTelemetry);
        onDeploySubmit({ ...deploymentInfo, tracker: deploymentTracker });
    };

    const handleDeploymentDeletion = () => {
        setIsDeleteModalHidden(true);
        onDeleteDeployment(selectedItems[0]?.deploymentName);
        selection.setAllSelected(false);
    };

    const dismissDeployModal = () => {
        logButtonClick(serviceName, 'DeployModel', 'CancelDeployment', null, projectIdForTelemetry);
        updateModalStates({ isDeployModalHidden: true });
        deploymentTracker.reset();
    };

    const emptyStateMessage = (
        <>
            {localize(messages.emptyMsg)}&nbsp;
            <RBACTooltip
                displayedUserRole={displayedUserRole}
                hidden={canDeployProject || noTrainedModelsFound}
                data-automation-id="deployModelLinkLanguageToolTip"
                componentSurroundedByToolTip={
                    <Link
                        disabled={!canDeployProject || noTrainedModelsFound}
                        onClick={() => updateModalStates({ isDeployModalHidden: false })}
                        styles={{ root: { color: emptyStateTheme.emptyStateLinkColor } }}>
                        {localize(messages.startDeployment)}
                    </Link>
                }
            />
        </>
    );

    const handleAddDeployment = () => {
        logButtonClick(serviceName, 'DeployModel', 'AddDeployment', null, projectIdForTelemetry);
        updateModalStates({ isDeployModalHidden: false });
    };

    const handleGetPredictionUrl = () => {
        logButtonClick(serviceName, 'DeployModel', 'PredictionUrl', null, projectIdForTelemetry);
        updateModalStates({ isEndpointURLModalHidden: false });
    };

    const handleDeleteDeployment = () => {
        logButtonClick(serviceName, 'DeployModel', 'DeleteDeployment', null, projectIdForTelemetry);
        setIsDeleteModalHidden(false);
    };

    const handleSwapDeployments = () => {
        logButtonClick(serviceName, 'DeployModel', 'SwapDeployment', null, projectIdForTelemetry);
        updateModalStates({ isSwapDeploymentsModalHidden: false });
    };

    React.useEffect(() => {
        if (resourceOptions.length && !modalStates.isEndpointURLModalHidden) {
            handleResourceChange(resourceOptions[0].key as string);
        }
    }, [resourceOptions, modalStates.isEndpointURLModalHidden]);

    React.useEffect(() => {
        if (selectedItems[0]) {
            const currentDeploymentStore = deployments.find(item => item.deploymentName === selectedItems[0].deploymentName);
            setResourceOptions(
                currentDeploymentStore.assignedResources.map(resource => {
                    const { resourceName } = getResourceInfo(resource.azureResourceId);

                    return {
                        key: resource.azureResourceId,
                        text: `${resource.region} - ${resourceName}`
                    };
                })
            );
        }
    }, [selectedItems]);

    return (
        <>
            <TableContainer verticalFill tokens={{ childrenGap: 5, padding: '5px 0' }}>
                <DeployedModelsTableCommandBar
                    onQueryChange={setQuery}
                    selectedItems={selectedItems}
                    onDeploy={handleAddDeployment}
                    onGetPredictionUrl={handleGetPredictionUrl}
                    onDeleteDeployment={handleDeleteDeployment}
                    isAddDeploymentButtonDisabled={noTrainedModelsFound}
                    onSwapDeployments={onSwapDeployments ? handleSwapDeployments : null}
                />

                {isInEmptyState ? (
                    <EmptyStateView imageStyles={{ image: { height: 150 } }} message={emptyStateMessage} />
                ) : (
                    <DeployedModelsTable
                        sortKey={sortKey}
                        selection={selection}
                        items={filteredModels}
                        areItemsFiltered={Boolean(query)}
                        overallItemsCount={filteredModels.length}
                        deletedDeploymentsIds={deletedDeploymentsIds}
                        isLoading={deploymentsStoreHouse.tracker.isRunning}
                        isMultiRegionDeploymentEnabled={isMultiRegionDeploymentEnabled}
                        onSortColumn={column => {
                            selection.setAllSelected(false);
                            setSortKey({
                                key: column.key as keyof DeploymentStore,
                                order: column.isSortedDescending ? 'asc' : 'desc'
                            });
                        }}
                    />
                )}
            </TableContainer>

            {onRenderDeployModal ? (
                onRenderDeployModal({
                    onSubmit: handleDeploy,
                    onDismiss: dismissDeployModal,
                    disabled: deploymentTracker.isRunning,
                    hidden: modalStates.isDeployModalHidden,
                    errorMessage: deploymentTracker.error && `${deploymentTracker.error?.name}: ${deploymentTracker.error?.message}`
                })
            ) : (
                <DeployModelModal
                    onSubmit={handleDeploy}
                    onDismiss={dismissDeployModal}
                    disabled={deploymentTracker.isRunning}
                    hidden={modalStates.isDeployModalHidden}
                    deploymentsStoreHouse={deploymentsStoreHouse}
                    isServiceKeyTypeValid={isServiceKeyTypeValid}
                    trainedModelStoreHouse={trainedModelStoreHouse}
                    regionDeploymentStoreHouse={regionDeploymentStoreHouse}
                    isMultiRegionDeploymentEnabled={isMultiRegionDeploymentEnabled}
                    isOrchestrationProjectConnectedToLUISOrQna={isOrchestrationProjectConnectedToLUISOrQna}
                    errorMessage={deploymentTracker.error && `${deploymentTracker.error?.name}: ${deploymentTracker.error?.message}`}
                />
            )}

            <DeployedModelUrlModal
                valid
                retrieveCode={retrieveCode}
                resourceOptions={resourceOptions}
                selectedResource={selectedResource}
                isLoadingResources={isLoadingResources}
                hidden={modalStates.isEndpointURLModalHidden}
                onChangeSelectedResource={handleResourceChange}
                showDeploymentRegions={isMultiRegionDeploymentEnabled}
                predictUrl={predictUrl(selectedItems[0]?.deploymentName)}
                submitCode={submitCode(selectedItems[0]?.deploymentName)}
                onDismiss={() => updateModalStates({ isEndpointURLModalHidden: true })}
            />

            <SwapDeploymentsModal
                hidden={modalStates.isSwapDeploymentsModalHidden}
                disabled={swapTracker.isRunning || deploymentsStoreHouse.tracker.isRunning}
                onDismiss={() => updateModalStates({ isSwapDeploymentsModalHidden: true })}
                onSubmit={() => onSwapDeployments(selectedItems[0]?.deploymentName, selectedItems[1]?.deploymentName, swapTracker)}
            />

            <DeletePromptModal
                hidden={isDeleteModalHidden}
                onDelete={handleDeploymentDeletion}
                onDismiss={() => setIsDeleteModalHidden(true)}
                dialogContentProps={{ title: localize(messages.deletingDeploymentTitle) }}>
                <LuisText>{localize(messages.deletingDeploymentBody)}</LuisText>
            </DeletePromptModal>

            <Stack verticalAlign="end">
                <RuntimeCardsSection
                    pageName="DeployModel"
                    featureName={serviceName}
                    projectId={projectIdForTelemetry}
                    sdkLink={getSdkLink(projectType)}
                    styles={runtimeCardsSectionStyles}
                    githubSamplesLinkOptions={getGithubSamples(projectType, localize(messages.runtimeGithubCardLinkText))}
                />
            </Stack>
        </>
    );
});
